import { LoginMethod } from '@choco-admin/types';

export type LoginMethodIcon =
  | 'phone'
  | 'email'
  | 'emailAndPhone'
  | 'sso'
  | 'unknown';
export type LoginMethodColor = 'default' | 'primary' | 'secondary';

export interface LoginMethodDisplayConfig {
  label: string;
  ariaLabel: string;
  icon: LoginMethodIcon;
  color: LoginMethodColor;
}

const normalizeLoginMethod = (loginMethod?: LoginMethod | string) =>
  loginMethod?.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

const formatUnknownLoginMethod = (loginMethod: string) =>
  loginMethod
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase()) || 'N/A';

export const getLoginMethodDisplayConfig = (
  loginMethod?: LoginMethod | string
): LoginMethodDisplayConfig => {
  const normalizedLoginMethod = normalizeLoginMethod(loginMethod);

  switch (normalizedLoginMethod) {
    case 'phone':
      return {
        label: 'Phone',
        ariaLabel: 'Phone login',
        icon: 'phone',
        color: 'default',
      };
    case 'email':
      return {
        label: 'Email',
        ariaLabel: 'Email login',
        icon: 'email',
        color: 'default',
      };
    case 'emailandphone':
      return {
        label: 'Email & Phone',
        ariaLabel: 'Email and phone login',
        icon: 'emailAndPhone',
        color: 'default',
      };
    case 'sso':
    case 'singlesignon':
    case 'saml':
    case 'federated':
      return {
        label: 'SSO',
        ariaLabel: 'Single sign-on login',
        icon: 'sso',
        color: 'secondary',
      };
    case 'google':
    case 'googleoauth':
      return {
        label: 'Google SSO',
        ariaLabel: 'Google single sign-on login',
        icon: 'sso',
        color: 'secondary',
      };
    default:
      return {
        label: loginMethod ? formatUnknownLoginMethod(loginMethod) : 'N/A',
        ariaLabel: loginMethod
          ? `${formatUnknownLoginMethod(loginMethod)} login`
          : 'Login method not available',
        icon: 'unknown',
        color: 'primary',
      };
  }
};

/**
 * Whether a user authenticates through single sign-on. SSO users' identity
 * details (language, email, account type, profile picture, linked
 * buyers/distributors) are sourced from the identity provider and are therefore
 * read-only in the admin portal; their name, phone number, and order
 * preferences remain portal-managed and stay editable.
 */
export const isSsoLoginMethod = (loginMethod?: LoginMethod | string): boolean =>
  getLoginMethodDisplayConfig(loginMethod).icon === 'sso';
